<template>
  <v-container fluid class="pa-0 ma-0">
    <div
      v-if="isHomePage"
      class="membership-header-row d-flex flex-wrap align-stretch justify-start"
    >
      <!-- Bloque 1: Miembro de (titular) / Colaborador de (submiembro) -->
      <div class="indicator-block pl-3 pr-3 py-3">
        <div class="neutral-block d-flex flex-column">
          <div class="indicator-title">
            {{ titularLabel }}:
          </div>
          <div class="d-flex align-center mt-2">
            <v-avatar
              v-if="hasTitularLogo"
              size="80"
              class="org-avatar"
            >
              <v-img :src="titularLogo" :alt="titularName" />
            </v-avatar>
            <v-avatar
              v-else
              size="80"
              color="#f0f1f4"
              class="org-avatar"
            >
              <v-icon size="40" color="#8c929e">mdi-image-off-outline</v-icon>
            </v-avatar>
          </div>
        </div>
      </div>

      <!-- Bloque 2: Estado -->
      <div class="indicator-block pl-3 pr-3 py-3">
        <v-card
          v-if="statusInfo"
          class="status-card pa-3 d-flex flex-column"
          :color="statusInfo.color"
          elevation="0"
          rounded="xl"
        >
          <div class="indicator-title">Estado:</div>
          <div class="indicator-content">
            {{ statusInfo.status }}
          </div>
        </v-card>
        <v-card
          v-else
          class="status-card pa-3 d-flex flex-column"
          color="#f2f2f2"
          elevation="0"
          rounded="xl"
        >
          <div class="indicator-title">Estado:</div>
          <div class="indicator-content">N/D</div>
        </v-card>
      </div>

      <!-- Bloque 3: Fecha de vencimiento -->
      <div class="indicator-block px-3 py-3">
        <v-card
          v-if="!disableExpirationDate"
          class="status-card pa-3 d-flex flex-column"
          :color="statusInfo ? statusInfo.color : '#f2f2f2'"
          elevation="0"
          rounded="xl"
        >
          <div class="indicator-title">Fecha de vencimiento:</div>
          <div class="indicator-content">
            {{ expirationDate || "N/D" }}
          </div>
        </v-card>
      </div>

      <!-- Bloque 4: Miembro de (asociación) — solo submiembro -->
      <div v-if="isSubmember" class="indicator-block px-3 py-3">
        <div class="neutral-block d-flex flex-column">
          <div class="indicator-title">Miembro de:</div>
          <div class="d-flex align-center mt-2">
            <v-avatar
              v-if="hasAssociationLogo"
              size="80"
              class="org-avatar"
            >
              <v-img :src="associationLogo" :alt="associationName" />
            </v-avatar>
            <v-avatar
              v-else
              size="80"
              color="#f0f1f4"
              class="org-avatar"
            >
              <v-icon size="40" color="#8c929e">mdi-image-off-outline</v-icon>
            </v-avatar>
            <span
              v-if="associationName"
              class="ml-3 indicator-name"
            >
              {{ associationName }}
            </span>
          </div>
        </div>
      </div>

      <!-- Acciones adicionales (botones derecha) -->
      <div
        class="indicator-block actions-block px-3 py-3 ml-auto d-flex flex-row flex-wrap align-center justify-end"
      >
        <v-tooltip
          open-on-hover
          bottom
          activator="#constancyButton"
          max-width="250"
        >
          <span
            >Si tiene problemas para descargar la constancia, le recomendamos
            intentar desde una ventana incógnito.</span
          >
        </v-tooltip>

        <MemberForm
          v-if="user && (user.role === 'MEMBER' || user.role === 'MEMBERSHIP_USER')"
          edit
          :member="user"
          :profilePicture="user.profilePicture"
          @update="reloadMemberInfo"
          class="ma-1"
        />

        <div
          id="constancyButton"
          v-if="user && user.validationStatus == 'ACTIVE' && user.hasDiplomaTemplate"
        >
          <v-btn
            color="#1B273E"
            class="ma-1 white--text font-weight-bold"
            rounded
            @click.prevent="downloadConstancy()"
          >
            Constancia de membresía
            <v-icon small class="ml-2">mdi-download</v-icon>
          </v-btn>
        </div>

        <v-btn
          v-if="
            user && user.headOffice &&
            (user.headOffice.id == '62b622c66a7441ae20159d7d' ||
              user.headOffice.id == '6297b0fec7bf8b6f826313be')
          "
          elevation="1"
          style="
            background-color: #70b578;
            border-radius: 50px;
            font-weight: bold;
            color: white;
          "
          class="ma-1 d-flex justify-center align-center"
          @click="redirectToValoresAMPI"
        >
          Ir a Valores AMPI
        </v-btn>
      </div>
    </div>

    <v-row v-if="isInIndulgencePeriod" class="mt-2">
      <v-col>
        <v-row class="my-0 mx-2">
          <v-icon color="red darken-3">mdi-alert</v-icon>
          <span class="font-weight-bold red--text text--darken-3">
            Estás en días de tolerancia
          </span>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import moment from "moment";
import { mapActions, mapMutations, mapState } from "vuex";
import { homologateStatusWithColor } from "@/common/hook/useHelper";
import { isOrganization } from "../../share/validateOrganization";
import MemberForm from "@/components/shared/MemberForm";

export default {
  name: "MembershipStatusHeader",
  components: {
    MemberForm,
  },
  data() {
    return {
      ampiId1: "62b622c66a7441ae20159d7d",
      ampiId2: "6297b0fec7bf8b6f826313be",
    };
  },
  computed: {
    ...mapState("user", ["user"]),
    ...mapState("member", ["credential"]),
    ...mapState("membershipUsers", ["credential", "ownerAffiliation"]),
    ...mapState("membershipProfile", ["userMembership"]),

    isHomePage() {
      const path = this.$route ? this.$route.path : "";
      return (
        path === "/member/home" ||
        path === "/member/home/" ||
        path.startsWith("/member/home/showInfo")
      );
    },

    isSubmember() {
      return this.user && this.user.role === "MEMBERSHIP_USER";
    },

    isAsofom() {
      const headOfficeId =
        this.user && this.user.headOffice
          ? typeof this.user.headOffice === "object"
            ? this.user.headOffice.id
            : this.user.headOffice
          : null;
      return isOrganization("asofom", null, headOfficeId);
    },

    activeCredential() {
      return this.isSubmember
        ? this.$store.state.membershipUsers.credential
        : this.$store.state.member.credential;
    },

    titularLabel() {
      return this.isSubmember ? "Colaborador de" : "Miembro de";
    },

    titularLogo() {
      if (this.isSubmember) {
        const titularUser =
          this.user && this.user.membership && typeof this.user.membership === "object"
            ? this.user.membership
            : null;
        if (titularUser) {
          return (
            titularUser.profilePicture ||
            titularUser.organizationProfilePicture ||
            titularUser.companyImage ||
            ""
          );
        }
        return (
          (this.user && this.user.profilePicture) ||
          (this.user && this.user.organizationProfilePicture) ||
          ""
        );
      }
      return this.associationLogo;
    },

    titularName() {
      if (this.isSubmember) {
        const titularUser =
          this.user && this.user.membership && typeof this.user.membership === "object"
            ? this.user.membership
            : null;
        if (titularUser && titularUser.company) {
          return titularUser.company;
        }
        if (this.user && this.user.company) {
          return this.user.company;
        }
        return "";
      }
      return this.user && this.user.company ? this.user.company : "";
    },

    hasTitularLogo() {
      const logo = this.titularLogo;
      return (
        !!logo &&
        logo !== "NOT_ASSIGNED" &&
        logo !== "N/D" &&
        logo !== null &&
        logo !== undefined &&
        String(logo).length > 0
      );
    },

    associationLogo() {
      if (this.user && this.user.headOffice) {
        if (typeof this.user.headOffice === "object") {
          return (
            this.user.headOffice.profilePicture ||
            this.user.headOffice.logo ||
            ""
          );
        }
      }
      return "";
    },

    associationName() {
      if (this.user && this.user.headOffice) {
        if (typeof this.user.headOffice === "object") {
          return (
            this.user.headOffice.name ||
            this.user.headOffice.company ||
            ""
          );
        }
      }
      return "";
    },

    hasAssociationLogo() {
      const logo = this.associationLogo;
      return (
        !!logo &&
        logo !== "NOT_ASSIGNED" &&
        logo !== "N/D" &&
        logo !== null &&
        logo !== undefined &&
        String(logo).length > 0
      );
    },

    statusInfo() {
      if (!this.user || !this.user.validationStatus) return null;
      return homologateStatusWithColor(this.user.validationStatus);
    },

    expirationDate() {
      if (!this.user) return null;

      const affiliationSource = this.resolveAffiliationSource();
      if (!affiliationSource) return null;

      let inactivationDate = null;

      if (
        affiliationSource.national &&
        affiliationSource.national.details
      ) {
        const details = affiliationSource.national.details;
        if (details.length > 0) {
          const affiliateInfo = details[0].affiliateInformation;
          if (affiliateInfo && affiliateInfo.inactiveDate) {
            inactivationDate = affiliateInfo.inactiveDate;
          }
        }
      }

      if (affiliationSource.local && affiliationSource.local.details) {
        const details = affiliationSource.local.details;
        if (details.length > 0) {
          const affiliateInfo = details[0].affiliateInformation;
          if (affiliateInfo && affiliateInfo.inactiveDate) {
            if (
              !inactivationDate ||
              affiliateInfo.inactiveDate < inactivationDate
            ) {
              inactivationDate = affiliateInfo.inactiveDate;
            }
          }
        }
      }

      if (!inactivationDate) return null;
      return moment(inactivationDate).format("MMMM DD YYYY");
    },

    disableExpirationDate() {
      const headOfficeId =
        this.user && this.user.headOffice
          ? typeof this.user.headOffice === "object"
            ? this.user.headOffice.id
            : this.user.headOffice
          : null;
      const club51Ids = ["676353f01ea3041cdd88bc26"];
      return club51Ids.includes(headOfficeId);
    },

    isInIndulgencePeriod() {
      if (!this.user) return false;

      const affiliationSource = this.resolveAffiliationSource();
      if (!affiliationSource) return false;

      const nationalDetails =
        (affiliationSource.national && affiliationSource.national.details) ||
        [];
      const localDetails =
        (affiliationSource.local && affiliationSource.local.details) || [];

      let nationalInactivationDate = null;
      if (nationalDetails.length > 0) {
        nationalInactivationDate =
          nationalDetails[0]?.affiliateInformation?.inactiveDate || null;
      }

      let localInactivationDate = null;
      if (localDetails.length > 0) {
        localInactivationDate =
          localDetails[0]?.affiliateInformation?.inactiveDate || null;
      }

      if (nationalDetails.length <= 0 && localDetails.length <= 0)
        return false;

      let nationalIndulgence = true;
      let localIndulgence = true;

      if (nationalInactivationDate) {
        nationalIndulgence = new Date(nationalInactivationDate) < new Date();
      }
      if (localInactivationDate) {
        localIndulgence = new Date(localInactivationDate) < new Date();
      }

      return nationalIndulgence && localIndulgence;
    },
  },
  methods: {
    ...mapMutations("notification", ["show"]),
    ...mapActions("user", ["realoadUserData"]),
    ...mapActions("member", ["fetchMemberCredentials"]),
    ...mapActions("membershipUsers", [
      "fetchMembershipCredentials",
      "fetchOwnerAffiliation",
    ]),
    homologateStatusWithColor,
    isOrganization,
    resolveAffiliationSource() {
      if (this.user && this.user.affiliation) {
        const hasOwnData =
          (this.user.affiliation.national &&
            this.user.affiliation.national.details &&
            this.user.affiliation.national.details.length > 0) ||
          (this.user.affiliation.local &&
            this.user.affiliation.local.details &&
            this.user.affiliation.local.details.length > 0);
        if (hasOwnData) {
          return this.user.affiliation;
        }
      }
      if (this.isSubmember && this.ownerAffiliation) {
        return this.ownerAffiliation.affiliation || null;
      }
      if (this.user && this.user.affiliation) {
        return this.user.affiliation;
      }
      return null;
    },
    async ensureCredentialsLoaded() {
      if (!this.user) return;
      const targetStore = this.isSubmember
        ? this.$store.state.membershipUsers
        : this.$store.state.member;
      if (
        !targetStore.credential ||
        !targetStore.credential.credentialInformation
      ) {
        try {
          if (this.isSubmember) {
            await this.fetchMembershipCredentials();
          } else {
            await this.fetchMemberCredentials();
          }
        } catch (err) {
          console.log("Error fetching credentials:", err);
        }
      }
      if (this.isSubmember && !this.ownerAffiliation) {
        try {
          await this.fetchOwnerAffiliation();
        } catch (err) {
          console.log("Error fetching owner affiliation:", err);
        }
      }
    },
    redirectToValoresAMPI() {
      window.open("https://valoresampi.mx/", "_blank");
    },
    async reloadMemberInfo() {
      if (this.user && this.user.email) {
        await this.realoadUserData({ email: this.user.email });
      }
    },
    async downloadConstancy() {
      try {
        const response = await this.axios({
          method: "POST",
          url: "/users/certificate",
          data: {
            userId: this.user._id,
          },
        });
        const constancyUrl =
          response && response.data ? response.data.constancy : null;
        if (constancyUrl && String(constancyUrl).trim() !== "") {
          window.location.href = constancyUrl;
        } else {
          this.show({
            text: "No se pudo generar la constancia en este momento. Por favor inténtalo de nuevo más tarde.",
            color: "error",
          });
        }
      } catch (error) {
        console.log(error);
        this.show({
          text: "No se pudo generar la constancia en este momento. Por favor inténtalo de nuevo más tarde.",
          color: "error",
        });
      }
    },
  },
  async mounted() {
    await this.ensureCredentialsLoaded();
  },
};
</script>

<style scoped>
.membership-header-row {
  background-color: transparent;
  padding: 0;
}

.indicator-block {
  min-width: 0;
}

.indicator-title {
  color: #1e2e3e;
  font-weight: 600;
  font-size: 13px;
  line-height: 1.2;
}

.indicator-content {
  color: #1e2e3e;
  font-weight: 900;
  min-height: 28px;
  font-size: 15px;
  margin-top: 8px;
}

.indicator-name {
  color: #1e2e3e;
  font-weight: 700;
  font-size: 15px;
  line-height: 1.2;
  word-break: break-word;
}

.status-card {
  min-width: 220px;
  min-height: 96px;
}

.neutral-block {
  padding-left: 0;
  background-color: transparent !important;
  border: none;
}

.neutral-card {
  min-width: 220px;
  min-height: 96px;
  background-color: #ffffff !important;
  border: 1px solid #e6e8ec;
}

.org-avatar {
  background-color: transparent !important;
  border: none;
  flex-shrink: 0;
}

.actions-block {
  min-width: 0;
}
</style>
